import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';

import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MyHead from '../../components/MyHead';
import Image from 'next/future/image';
import { buttonVariants } from '@/components/v2/UI/Button';
import Link from 'next/link';
import LinkTo from '@/components/LinkTo';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar } from 'swiper';
import { motion, useScroll, useTransform } from 'framer-motion';

const TechnologiesPage: React.FC = () => {
  const { t } = useTranslation();

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  const freedomCards = useMemo(
    () => [
      {
        title: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-1.TITLE'),
        description: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-1.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-1.BTN'),
        src: '/v2/technologies/freedom/hypermotion.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-2.TITLE'),
        description: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-2.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-2.BTN'),
        src: '/v2/technologies/freedom/freelease.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-3.TITLE'),
        description: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-3.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-3.BTN'),
        src: '/v2/technologies/freedom/flexy-ribs.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-4.TITLE'),
        description: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-4.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-4.BTN'),
        src: '/v2/technologies/freedom/ergomotion.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-5.TITLE'),
        description: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-5.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.FREEDOM.CARDS.CARD-5.BTN'),
        src: '/v2/technologies/freedom/ultramotion.jpg'
      }
    ],
    []
  );
  const temperatureCards = useMemo(
    () => [
      {
        title: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-1.TITLE'),
        description: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-1.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-1.BTN'),
        src: '/v2/technologies/temperature/coolvent.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-2.TITLE'),
        description: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-2.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-2.BTN'),
        src: '/v2/technologies/temperature/heatmemory.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-3.TITLE'),
        description: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-3.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-3.BTN'),
        src: '/v2/technologies/temperature/haloflex.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-4.TITLE'),
        description: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-4.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-4.BTN'),
        src: '/v2/technologies/temperature/buffercone.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-5.TITLE'),
        description: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-5.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-5.BTN'),
        src: '/v2/technologies/temperature/flowtunnels.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-6.TITLE'),
        description: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-6.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.TEMPERATURE.CARDS.CARD-6.BTN'),
        src: '/v2/technologies/temperature/drylight.jpg'
      }
    ],
    []
  );
  const weatherCards = useMemo(
    () => [
      {
        title: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-1.TITLE'),
        description: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-1.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-1.BTN'),
        src: '/v2/technologies/weather/membrain-115.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-2.TITLE'),
        description: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-2.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-2.BTN'),
        src: '/v2/technologies/weather/airnest.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-3.TITLE'),
        description: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-3.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.WEATHER.CARDS.CARD-3.BTN'),
        src: '/v2/technologies/weather/airdual.jpg'
      }
    ],
    []
  );
  const socksCards = useMemo(
    () => [
      {
        title: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-1.TITLE'),
        description: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-1.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-1.BTN'),
        src: '/v2/technologies/socks/zero-cuff.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-2.TITLE'),
        description: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-2.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-2.BTN'),
        src: '/v2/technologies/socks/high-5.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-3.TITLE'),
        description: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-3.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.SOCKS.CARDS.CARD-3.BTN'),
        src: '/v2/technologies/socks/double-layer-system.jpg'
      }
    ],
    []
  );
  const shoesCards = useMemo(
    () => [
      // {
      //   title: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-1.TITLE'),
      //   description: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-1.DESC'),
      //   link: '#',
      //   btntext: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-1.BTN'),
      //   src: '/v2/technologies/shoes/megacushioning.jpg'
      // },
      {
        title: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-2.TITLE'),
        description: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-2.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-2.BTN'),
        src: '/v2/technologies/shoes/hoof-heel-sole.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-3.TITLE'),
        description: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-3.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-3.BTN'),
        src: '/v2/technologies/shoes/u-control-sole.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-4.TITLE'),
        description: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-4.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-4.BTN'),
        src: '/v2/technologies/shoes/u-grip-sole.jpg'
      },
      {
        title: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-5.TITLE'),
        description: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-5.DESC'),
        link: '#',
        btntext: t('P_TECHNOLOGIES.SHOES.CARDS.CARD-5.BTN'),
        src: '/v2/technologies/shoes/vibram-sole.jpg'
      }
    ],
    []
  );

  return (
    <div className="bg-[#061C22]">
      <MyHead />

      <div className="section relative full bg-[#061C22] text-white overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#0e3842] to-transparent z-10"></div>
        <motion.div
          className="hidden md:block absolute inset-0 z-0"
          style={{
            backgroundImage: `url(/v2/technologies/background.jpg)`,
            backgroundPosition: 'bottom',
            backgroundSize: 'cover',
            y: bgY
          }}
        />
        <motion.div
          className="block md:hidden absolute inset-0 z-0"
          style={{
            backgroundImage: `url(/v2/technologies/background-m.jpg)`,
            backgroundPosition: 'bottom',
            backgroundSize: 'cover',
            y: bgY
          }}
        />
        <div className="section-wrapper relative z-20">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="col-span-12 md:text-center md:section mt-10 md:mt-0">
              <div className="md:text-[10vw] text-[9vw] font-black leading-tight tracking-uyn uppercase">
                {t('P_TECHNOLOGIES.SECTION-1.TITLE')}
              </div>
            </div>

            <div className="col-span-12 md:col-span-5">
              <div className="h3">{t('P_TECHNOLOGIES.SECTION-1.SUBTITLE')}</div>
              <div className="text-body">
                {t('P_TECHNOLOGIES.SECTION-1.DESC')}
              </div>
            </div>
          </div>
        </div>
        <div className="section-wrapper relative pt-20 z-20">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="col-span-12 text-center">
              <div className="h4 max-w-[920px] mx-auto">
                {t('P_TECHNOLOGIES.SECTION-2.TITLE')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-gradient-to-t from-[#061C22] to-[#0e3842] text-white overflow-hidden">
        <div className="section-wrapper relative">
          <div className="grid gap-y-8 grid-cols-12 md:gap-0 clip-swiper-left">
            <div className="md:col-span-4 col-span-12 flex flex-col justify-center relative md:pl-8 md:bg-gradient-to-t from-black to-[#0e3842] md:pr-8 h-full z-40">
              <div className="h2">{t('P_TECHNOLOGIES.FREEDOM.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_TECHNOLOGIES.FREEDOM.DESC')}
              </div>
            </div>
            <div className="col-span-12 md:col-span-8 relative z-20">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                navigation={true}
                modules={[Scrollbar, Navigation]}
                scrollbar={{ hide: false }}
                className="swiperScrollbarTechs noArrowsDesk"
                breakpoints={{
                  768: {
                    slidesPerView: 2.2
                  },
                  1024: {
                    slidesPerView: 2.5
                  }
                }}
              >
                {freedomCards.map((card, idx) => (
                  <SwiperSlide key={idx} tag="li" className="relative">
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />

                    <div className="pt-6">
                      <h3 className="h5">{card.title}</h3>
                      <p className="">{card.description}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#061C22] text-white overflow-hidden">
        <div className="section-wrapper relative">
          <div className="grid gap-y-8 grid-cols-12 md:gap-0 clip-swiper-left">
            <div className="md:col-span-4 col-span-12 flex flex-col justify-center relative md:pl-8 md:bg-[#061C22] md:pr-8 h-full z-40">
              <div className="h2">{t('P_TECHNOLOGIES.TEMPERATURE.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_TECHNOLOGIES.TEMPERATURE.DESC')}
              </div>
            </div>
            <div className="col-span-12 md:col-span-8 relative z-20">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                navigation={true}
                modules={[Scrollbar, Navigation]}
                scrollbar={{ hide: false }}
                className="swiperScrollbarTechs noArrowsDesk"
                breakpoints={{
                  768: {
                    slidesPerView: 2.2
                  },
                  1024: {
                    slidesPerView: 2.5
                  }
                }}
              >
                {temperatureCards.map((card, idx) => (
                  <SwiperSlide key={idx} tag="li" className="relative">
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />

                    <div className="pt-6">
                      <h3 className="h5">{card.title}</h3>
                      <p className="">{card.description}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#061C22] text-white overflow-hidden">
        <div className="section-wrapper relative">
          <div className="grid gap-y-8 grid-cols-12 md:gap-0 clip-swiper-left">
            <div className="md:col-span-4 col-span-12 flex flex-col justify-center relative md:pl-8 md:bg-[#061C22] md:pr-8 h-full z-40">
              <div className="h2">{t('P_TECHNOLOGIES.WEATHER.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_TECHNOLOGIES.WEATHER.DESC')}
              </div>
            </div>
            <div className="col-span-12 md:col-span-8 relative z-20">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                navigation={true}
                modules={[Scrollbar, Navigation]}
                scrollbar={{ hide: false }}
                className="swiperScrollbarTechs noArrowsDesk"
                breakpoints={{
                  768: {
                    slidesPerView: 2.2
                  },
                  1024: {
                    slidesPerView: 2.5
                  }
                }}
              >
                {weatherCards.map((card, idx) => (
                  <SwiperSlide key={idx} tag="li" className="relative">
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />

                    <div className="pt-6">
                      <h3 className="h5">{card.title}</h3>
                      <p className="">{card.description}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#061C22] text-white overflow-hidden">
        <div className="section-wrapper relative">
          <div className="grid gap-y-8 grid-cols-12 md:gap-0 clip-swiper-left">
            <div className="md:col-span-4 col-span-12 flex flex-col justify-center relative md:pl-8 md:bg-[#061C22] md:pr-8 h-full z-40">
              <div className="h2">{t('P_TECHNOLOGIES.SOCKS.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_TECHNOLOGIES.SOCKS.DESC')}
              </div>
            </div>
            <div className="col-span-12 md:col-span-8 relative z-20">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                navigation={true}
                modules={[Scrollbar, Navigation]}
                scrollbar={{ hide: false }}
                className="swiperScrollbarTechs noArrowsDesk"
                breakpoints={{
                  768: {
                    slidesPerView: 2.2
                  },
                  1024: {
                    slidesPerView: 2.5
                  }
                }}
              >
                {socksCards.map((card, idx) => (
                  <SwiperSlide key={idx} tag="li" className="relative">
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />

                    <div className="pt-6">
                      <h3 className="h5">{card.title}</h3>
                      <p className="">{card.description}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#061C22] text-white overflow-hidden">
        <div className="section-wrapper relative">
          <div className="grid gap-y-8 grid-cols-12 md:gap-0 clip-swiper-left">
            <div className="md:col-span-4 col-span-12 flex flex-col justify-center relative md:pl-8 md:bg-[#061C22] md:pr-8 h-full z-40">
              <div className="h2">{t('P_TECHNOLOGIES.SHOES.TITLE')}</div>
              <div className="text-body mt-6">
                {t('P_TECHNOLOGIES.SHOES.DESC')}
              </div>
            </div>
            <div className="col-span-12 md:col-span-8 relative z-20">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                navigation={true}
                modules={[Scrollbar, Navigation]}
                scrollbar={{ hide: false }}
                className="swiperScrollbarTechs noArrowsDesk"
                breakpoints={{
                  768: {
                    slidesPerView: 2.2
                  },
                  1024: {
                    slidesPerView: 2.5
                  }
                }}
              >
                {shoesCards.map((card, idx) => (
                  <SwiperSlide key={idx} tag="li" className="relative">
                    <Image
                      src={card.src}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full"
                    />

                    <div className="pt-6">
                      <h3 className="h5">{card.title}</h3>
                      <p className="">{card.description}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-[#061C22] text-white overflow-hidden">
        <div className="section-wrapper relative md:p-16 p-6 bg-white overflow-hidden">
          <Image
            src="/v2/technologies/cta.jpg"
            alt=""
            width={0}
            height={0}
            sizes="100vw"
            className="w-full h-full absolute top-0 left-0 object-cover object-center z-10"
          />
          <div className="relative z-20">
            <div className="h3">{t('P_TECHNOLOGIES.SECTION-FINAL.TITLE')}</div>
            <div className="text-body mt-4 max-w-[640px]">
              {t('P_TECHNOLOGIES.SECTION-FINAL.DESC')}
            </div>
            <div className="flex gap-2 flex-wrap mt-8">
              <LinkTo
                href="/materials"
                aria-label={t('P_TECHNOLOGIES.SECTION-FINAL.BTN')}
                className={buttonVariants({ variant: 'light' })}
              >
                {t('P_TECHNOLOGIES.SECTION-FINAL.BTN')}
              </LinkTo>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologiesPage;
